import {queryOptions} from '@github-ui/react-query'
import type {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {SubIssuesListItemNestedSubIssuesServerQuery} from '@github-ui/server-persisted-queries'
import type {SubIssuesListItemNestedSubIssuesServerQueryQuery} from '@github-ui/server-persisted-queries/types/SubIssuesListItemNestedSubIssuesServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export const NESTED_SUB_ISSUES_QUERY_KEY_PREFIX = ['issue-viewer', 'nested-sub-issues'] as const
export const NESTED_SUB_ISSUES_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14

export function nestedSubIssuesQueryKey(owner: string, repo: string, number: number) {
  return [...NESTED_SUB_ISSUES_QUERY_KEY_PREFIX, {owner, repo, number}] as const
}

// Built at module scope so a cached `queryFn` retains only these arguments, not a component's closure scope.
export function nestedSubIssuesQueryFn(owner: string, repo: string, number: number) {
  return ({signal}: {signal: AbortSignal}) => fetchNestedSubIssues(owner, repo, number, signal)
}

export function nestedSubIssuesQueryOptions(
  owner: string,
  repo: string,
  number: number,
  persister: ReturnType<typeof createServerPersistedQueryPersister>,
) {
  return queryOptions({
    queryKey: nestedSubIssuesQueryKey(owner, repo, number),
    queryFn: nestedSubIssuesQueryFn(owner, repo, number),
    gcTime: NESTED_SUB_ISSUES_CACHE_TTL_MS,
    persister,
  })
}

export async function fetchNestedSubIssues(
  owner: string,
  repo: string,
  number: number,
  signal?: AbortSignal,
): Promise<SubIssuesListItemNestedSubIssuesServerQueryQuery> {
  const response = await reactFetchJSON(
    `/${owner}/${repo}/issues/${number}/nested_sub_issues?queryId=${SubIssuesListItemNestedSubIssuesServerQuery}`,
    {signal},
  )
  if (!response.ok) {
    throw new Error(`Nested sub-issues fetch failed (${response.status})`)
  }

  const json = (await response.json()) as {
    data?: SubIssuesListItemNestedSubIssuesServerQueryQuery
    errors?: unknown[]
  }
  if (json.errors?.length || !json.data) {
    throw new Error('Nested sub-issues query returned errors')
  }

  return json.data
}
